import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchContactsWithStats } from "actions/rfqNotifications";
import rfqNotificationsService from "services/rfqNotifications.service";
import { nextSortDirection, getContactSortField } from "../utils/sortUtils";

const useContactSelection = (initialSelectedContacts = [], options = {}) => {
  const {
    enableSelectedContactsPagination = false,
    autoFetchOnMount = true,
    searchDebounceMs = 300,
    limit = 20,
    hasRecentActivity = true,
    sourceIds = [],
  } = options;

  // Contact search and pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedContactIds, setSelectedContactIds] = useState(
    new Set(initialSelectedContacts || []),
  );
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allContacts, setAllContacts] = useState([]);

  // Sort state for available contacts panel
  const [sortDirection, setSortDirection] = useState(null);
  const [sortPeriod, setSortPeriod] = useState("7d");

  // Sort state for selected contacts panel
  const [selectedSortDirection, setSelectedSortDirection] = useState(null);
  const [selectedSortPeriod, setSelectedSortPeriod] = useState("7d");

  // Selected contacts search and pagination state (for RuleForm)
  const [selectedSearchQuery, setSelectedSearchQuery] = useState("");
  const [debouncedSelectedSearchQuery, setDebouncedSelectedSearchQuery] =
    useState("");
  const [selectedContactsData, setSelectedContactsData] = useState([]);
  const [selectedContactsOffset, setSelectedContactsOffset] = useState(0);
  const [selectedContactsHasMore, setSelectedContactsHasMore] = useState(false);
  const [selectedContactsLoading, setSelectedContactsLoading] = useState(false);
  const lastFetchedContactIds = useRef(null);
  const lastSearchQueryRef = useRef("");
  const lastProcessedContactsRef = useRef(null);

  // AbortControllers for request cancellation
  const availableAbortRef = useRef(null);
  const selectedAbortRef = useRef(null);

  const dispatch = useDispatch();
  const { contacts, contactsLoading, contactsError } = useSelector(
    (state) => state.rfqNotifications,
  );

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [searchQuery, searchDebounceMs]);

  // Debounce selected contacts search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSelectedSearchQuery(selectedSearchQuery);
    }, searchDebounceMs);

    return () => clearTimeout(timer);
  }, [selectedSearchQuery, searchDebounceMs]);

  // Derive sort params for available contacts
  const availableSortField = sortDirection
    ? getContactSortField(sortPeriod)
    : "";
  const availableSortDir = sortDirection || "";

  // Fetch contacts when search query, filter parameters, or sort changes
  useEffect(() => {
    if (autoFetchOnMount) {
      // Abort previous request
      if (availableAbortRef.current) {
        availableAbortRef.current.abort();
      }
      const controller = new AbortController();
      availableAbortRef.current = controller;

      setCurrentOffset(0);
      setAllContacts([]);
      setHasMore(true);
      lastProcessedContactsRef.current = null;
      dispatch(
        fetchContactsWithStats(
          debouncedSearchQuery,
          limit,
          0,
          hasRecentActivity,
          sourceIds,
          availableSortField,
          availableSortDir,
          controller.signal,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearchQuery,
    dispatch,
    autoFetchOnMount,
    limit,
    hasRecentActivity,
    JSON.stringify(sourceIds),
    availableSortField,
    availableSortDir,
  ]);

  // Update contacts list when new data is fetched
  useEffect(() => {
    if (contacts && contacts.results) {
      const contactsKey = `${currentOffset}_${contacts.results.length}_${contacts.results.map((c) => c.id).join(",")}`;

      if (lastProcessedContactsRef.current === contactsKey) {
        return;
      }

      lastProcessedContactsRef.current = contactsKey;

      if (currentOffset === 0) {
        setAllContacts(contacts.results);
      } else {
        setAllContacts((prev) => {
          const existingIds = new Set(prev.map((contact) => contact.id));
          const newContacts = contacts.results.filter(
            (contact) =>
              !existingIds.has(contact.id) &&
              contact.id !== null &&
              contact.id !== undefined,
          );
          return [...prev, ...newContacts];
        });
      }
      setHasMore(contacts.results.length === limit);
    }
  }, [contacts, currentOffset, limit]);

  // Derive sort params for selected contacts
  const selectedSortField = selectedSortDirection
    ? getContactSortField(selectedSortPeriod)
    : "";
  const selectedSortDir = selectedSortDirection || "";

  // Function to fetch selected contacts data (for RuleForm)
  const fetchSelectedContactsData = useCallback(
    async (contactIds, offset = 0, append = false, searchQuery = "") => {
      if (!contactIds || contactIds.length === 0) {
        setSelectedContactsData([]);
        setSelectedContactsHasMore(false);
        return;
      }

      // Abort previous selected contacts request
      if (selectedAbortRef.current) {
        selectedAbortRef.current.abort();
      }
      const controller = new AbortController();
      selectedAbortRef.current = controller;

      setSelectedContactsLoading(true);

      try {
        const data = await rfqNotificationsService.getSelectedContacts(
          contactIds,
          limit,
          offset,
          searchQuery,
          false,
          [],
          selectedSortField,
          selectedSortDir,
          controller.signal,
        );
        const newResults = data.results || [];

        if (append) {
          setSelectedContactsData((prev) => [...prev, ...newResults]);
        } else {
          setSelectedContactsData(newResults);
        }

        setSelectedContactsHasMore(
          newResults.length === limit && offset + limit < contactIds.length,
        );
        setSelectedContactsLoading(false);
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          // Aborted by a newer request — do NOT touch the loading flag,
          // the new request has already set it to true.
          return;
        }
        console.error("Failed to fetch selected contacts data:", error);
        if (!append) {
          setSelectedContactsData([]);
          setSelectedContactsHasMore(false);
        }
        setSelectedContactsLoading(false);
      }
    },
    [limit, selectedSortField, selectedSortDir],
  );

  // Fetch selected contacts data when selectedContactIds, search query, or sort changes
  useEffect(() => {
    const contactIds = Array.from(selectedContactIds).filter(
      (id) => id !== null && id !== undefined,
    );

    const currentIdsString = contactIds.sort().join(",");
    const lastIdsString = lastFetchedContactIds.current;
    const lastSearchQuery = lastSearchQueryRef.current || "";

    const shouldFetch =
      currentIdsString !== lastIdsString ||
      debouncedSelectedSearchQuery !== lastSearchQuery;

    if (shouldFetch) {
      lastFetchedContactIds.current = currentIdsString;
      lastSearchQueryRef.current = debouncedSelectedSearchQuery;
      setSelectedContactsOffset(0);
      fetchSelectedContactsData(
        contactIds,
        0,
        false,
        debouncedSelectedSearchQuery,
      );
    }
  }, [
    selectedContactIds,
    debouncedSelectedSearchQuery,
    fetchSelectedContactsData,
  ]);

  // Re-fetch selected contacts when sort changes (without ID/search change)
  const prevSelectedSortRef = useRef({ field: "", dir: "" });
  useEffect(() => {
    const prev = prevSelectedSortRef.current;
    if (
      prev.field !== selectedSortField ||
      prev.dir !== selectedSortDir
    ) {
      prevSelectedSortRef.current = {
        field: selectedSortField,
        dir: selectedSortDir,
      };
      const contactIds = Array.from(selectedContactIds).filter(
        (id) => id !== null && id !== undefined,
      );
      if (contactIds.length > 0) {
        setSelectedContactsOffset(0);
        fetchSelectedContactsData(
          contactIds,
          0,
          false,
          debouncedSelectedSearchQuery,
        );
      }
    }
  }, [
    selectedSortField,
    selectedSortDir,
    selectedContactIds,
    debouncedSelectedSearchQuery,
    fetchSelectedContactsData,
  ]);

  // Sort handlers for available contacts
  const handleSortClick = useCallback(() => {
    setSortDirection((prev) => nextSortDirection(prev));
  }, []);

  const handlePeriodChange = useCallback(
    (period) => {
      setSortPeriod(period);
    },
    [],
  );

  // Sort handlers for selected contacts
  const handleSelectedSortClick = useCallback(() => {
    setSelectedSortDirection((prev) => nextSortDirection(prev));
  }, []);

  const handleSelectedPeriodChange = useCallback(
    (period) => {
      setSelectedSortPeriod(period);
    },
    [],
  );

  // Contact selection handlers
  const handleLoadMore = useCallback(() => {
    if (!contactsLoading && hasMore) {
      // Abort previous request
      if (availableAbortRef.current) {
        availableAbortRef.current.abort();
      }
      const controller = new AbortController();
      availableAbortRef.current = controller;

      const newOffset = currentOffset + limit;
      setCurrentOffset(newOffset);
      dispatch(
        fetchContactsWithStats(
          debouncedSearchQuery,
          limit,
          newOffset,
          hasRecentActivity,
          sourceIds,
          availableSortField,
          availableSortDir,
          controller.signal,
        ),
      );
    }
  }, [
    contactsLoading,
    hasMore,
    currentOffset,
    debouncedSearchQuery,
    dispatch,
    limit,
    hasRecentActivity,
    sourceIds,
    availableSortField,
    availableSortDir,
  ]);

  const handleLoadMoreSelected = useCallback(() => {
    if (!selectedContactsLoading && selectedContactsHasMore) {
      const newOffset = selectedContactsOffset + limit;
      setSelectedContactsOffset(newOffset);
      const contactIds = Array.from(selectedContactIds).filter(
        (id) => id !== null && id !== undefined,
      );
      fetchSelectedContactsData(
        contactIds,
        newOffset,
        true,
        debouncedSelectedSearchQuery,
      );
    }
  }, [
    selectedContactsLoading,
    selectedContactsHasMore,
    selectedContactsOffset,
    selectedContactIds,
    fetchSelectedContactsData,
    limit,
    debouncedSelectedSearchQuery,
  ]);

  const handleContactToggle = useCallback((contactId) => {
    if (contactId === null || contactId === undefined) {
      return;
    }

    setSelectedContactIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAllVisible = useCallback(() => {
    const visibleContactIds = allContacts
      .map((contact) => contact.id)
      .filter((id) => id !== null && id !== undefined);
    const allSelected = visibleContactIds.every((id) =>
      selectedContactIds.has(id),
    );

    if (allSelected) {
      setSelectedContactIds((prev) => {
        const newSet = new Set(prev);
        visibleContactIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
    } else {
      setSelectedContactIds((prev) => {
        const newSet = new Set(prev);
        visibleContactIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    }
  }, [allContacts, selectedContactIds]);

  const handleRemoveContact = useCallback((contactId) => {
    setSelectedContactIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(contactId);
      return newSet;
    });
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedContactIds(new Set());
  }, []);

  const handleReset = useCallback(() => {
    setSelectedContactIds(new Set());
    setSelectedContactsData([]);
    setSelectedContactsOffset(0);
    setSelectedContactsHasMore(false);
    setSelectedContactsLoading(false);
    setSearchQuery("");
    setSelectedSearchQuery("");
    setCurrentOffset(0);
    setHasMore(true);
    setAllContacts([]);
    setSortDirection(null);
    setSortPeriod("7d");
    setSelectedSortDirection(null);
    setSelectedSortPeriod("7d");
    lastFetchedContactIds.current = null;
  }, []);

  // Cleanup abort controllers on unmount
  useEffect(() => {
    return () => {
      if (availableAbortRef.current) {
        availableAbortRef.current.abort();
      }
      if (selectedAbortRef.current) {
        selectedAbortRef.current.abort();
      }
    };
  }, []);

  // Calculate available contacts count
  const availableContactsCount = allContacts
    .filter((contact) => contact.id !== null && contact.id !== undefined)
    .filter((contact) => !selectedContactIds.has(contact.id)).length;

  const latestBatchCount = contacts?.results
    ? contacts.results.filter(
        (contact) =>
          contact.id !== null &&
          contact.id !== undefined &&
          !selectedContactIds.has(contact.id),
      ).length
    : 0;

  const allVisibleSelected = (() => {
    const visibleContactIds = allContacts
      .map((contact) => contact.id)
      .filter((id) => id !== null && id !== undefined);
    return (
      visibleContactIds.length > 0 &&
      visibleContactIds.every((id) => selectedContactIds.has(id))
    );
  })();

  return {
    // State
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    selectedSearchQuery,
    setSelectedSearchQuery,
    debouncedSelectedSearchQuery,
    selectedContactIds,
    setSelectedContactIds,
    currentOffset,
    hasMore,
    allContacts,
    selectedContactsData,
    selectedContactsOffset,
    selectedContactsHasMore,
    selectedContactsLoading,
    availableContactsCount,
    latestBatchCount,
    allVisibleSelected,

    // Sort state - available panel
    sortDirection,
    sortPeriod,

    // Sort state - selected panel
    selectedSortDirection,
    selectedSortPeriod,

    // Redux state
    contacts,
    contactsLoading,
    contactsError,

    // Handlers
    handleLoadMore,
    handleLoadMoreSelected,
    handleContactToggle,
    handleSelectAllVisible,
    handleRemoveContact,
    handleDeselectAll,
    handleReset,

    // Sort handlers - available panel
    handleSortClick,
    handlePeriodChange,

    // Sort handlers - selected panel
    handleSelectedSortClick,
    handleSelectedPeriodChange,

    // Utilities
    fetchSelectedContactsData,
    limit,
  };
};

export default useContactSelection;
